/**
 * Canonical list of asset slots referenced by the public sites' JSON
 * content. Updated whenever a site adds a new image_slot / asset_slot
 * field. Drives the /admin/assets coverage indicator and the auto-slot
 * detection on drag-drop upload.
 *
 * Last sync : 2026-05-01 (extracted from
 * packages/site-defaults/src/sites/wc-en.json + the 13 broadcaster
 * codes + 9 channel codes + 8 testimonial avatars + 5 hero showcase).
 *
 * Total = 97 slots for wc-en alone.
 */
export interface RequiredSlot {
  slot: string;
  category:
    | 'hero'
    | 'cities'
    | 'classics'
    | 'docs'
    | 'vod'
    | 'broadcasters'
    | 'channels'
    | 'testimonials'
    | 'wc-rail';
  /** Suggested aspect ratio. Used as a hint in the UI. */
  aspect?: '16:9' | '1:1' | '2:3' | 'wide';
  /** Human-friendly description for the dashboard. */
  description?: string;
}

export const REQUIRED_SLOTS: RequiredSlot[] = [
  // Hero showcase (5)
  { slot: 'hero_fifa_wc', category: 'hero', aspect: '16:9' },
  { slot: 'hero_nfl_sunday', category: 'hero', aspect: '16:9' },
  { slot: 'hero_nba_playoffs', category: 'hero', aspect: '16:9' },
  { slot: 'hero_epl', category: 'hero', aspect: '16:9' },
  { slot: 'hero_f1', category: 'hero', aspect: '16:9' },

  // Host cities (16)
  { slot: 'city_atlanta', category: 'cities', aspect: '16:9' },
  { slot: 'city_boston', category: 'cities', aspect: '16:9' },
  { slot: 'city_dallas', category: 'cities', aspect: '16:9' },
  { slot: 'city_houston', category: 'cities', aspect: '16:9' },
  { slot: 'city_kc', category: 'cities', aspect: '16:9' },
  { slot: 'city_la', category: 'cities', aspect: '16:9' },
  { slot: 'city_miami', category: 'cities', aspect: '16:9' },
  { slot: 'city_nynj', category: 'cities', aspect: '16:9' },
  { slot: 'city_philly', category: 'cities', aspect: '16:9' },
  { slot: 'city_sf', category: 'cities', aspect: '16:9' },
  { slot: 'city_seattle', category: 'cities', aspect: '16:9' },
  { slot: 'city_toronto', category: 'cities', aspect: '16:9' },
  { slot: 'city_vancouver', category: 'cities', aspect: '16:9' },
  { slot: 'city_mexico', category: 'cities', aspect: '16:9' },
  { slot: 'city_guadalajara', category: 'cities', aspect: '16:9' },
  { slot: 'city_monterrey', category: 'cities', aspect: '16:9' },

  // Classic matches (8)
  { slot: 'classic_arg_fra_2022', category: 'classics', aspect: '16:9' },
  { slot: 'classic_bra_ger_2014', category: 'classics', aspect: '16:9' },
  { slot: 'classic_fra_cro_2018', category: 'classics', aspect: '16:9' },
  { slot: 'classic_esp_ned_2010', category: 'classics', aspect: '16:9' },
  { slot: 'classic_ita_fra_2006', category: 'classics', aspect: '16:9' },
  { slot: 'classic_usa_alg_2010', category: 'classics', aspect: '16:9' },
  { slot: 'classic_mex_ger_2018', category: 'classics', aspect: '16:9' },
  { slot: 'classic_mar_por_2022', category: 'classics', aspect: '16:9' },

  // WC rail trailers (6)
  { slot: 'rail_wc_trailer', category: 'wc-rail', aspect: '16:9' },
  { slot: 'rail_wc_teaser', category: 'wc-rail', aspect: '16:9' },
  { slot: 'rail_wc_draw', category: 'wc-rail', aspect: '16:9' },
  { slot: 'rail_wc_road', category: 'wc-rail', aspect: '16:9' },
  { slot: 'rail_wc_captains', category: 'wc-rail', aspect: '16:9' },
  { slot: 'rail_wc_predictions', category: 'wc-rail', aspect: '16:9' },

  // Sports docs (8 portrait posters)
  { slot: 'doc_drive_to_survive', category: 'docs', aspect: '2:3' },
  { slot: 'doc_last_dance', category: 'docs', aspect: '2:3' },
  { slot: 'doc_wrexham', category: 'docs', aspect: '2:3' },
  { slot: 'doc_quarterback', category: 'docs', aspect: '2:3' },
  { slot: 'doc_beckham', category: 'docs', aspect: '2:3' },
  { slot: 'doc_full_swing', category: 'docs', aspect: '2:3' },
  { slot: 'doc_all_or_nothing', category: 'docs', aspect: '2:3' },
  { slot: 'doc_senna', category: 'docs', aspect: '2:3' },

  // Trending VOD (24 portrait — the 4 collections combined)
  { slot: 'vod_drive_to_survive', category: 'vod', aspect: '2:3' },
  { slot: 'vod_last_dance', category: 'vod', aspect: '2:3' },
  { slot: 'vod_wrexham', category: 'vod', aspect: '2:3' },
  { slot: 'vod_quarterback', category: 'vod', aspect: '2:3' },
  { slot: 'vod_beckham', category: 'vod', aspect: '2:3' },
  { slot: 'vod_full_swing', category: 'vod', aspect: '2:3' },
  { slot: 'vod_dune2', category: 'vod', aspect: '2:3' },
  { slot: 'vod_oppenheimer', category: 'vod', aspect: '2:3' },
  { slot: 'vod_topgun_maverick', category: 'vod', aspect: '2:3' },
  { slot: 'vod_avatar2', category: 'vod', aspect: '2:3' },
  { slot: 'vod_gladiator2', category: 'vod', aspect: '2:3' },
  { slot: 'vod_wicked', category: 'vod', aspect: '2:3' },
  { slot: 'vod_hotd', category: 'vod', aspect: '2:3' },
  { slot: 'vod_tlou', category: 'vod', aspect: '2:3' },
  { slot: 'vod_severance', category: 'vod', aspect: '2:3' },
  { slot: 'vod_the_bear', category: 'vod', aspect: '2:3' },
  { slot: 'vod_squid_game', category: 'vod', aspect: '2:3' },
  { slot: 'vod_slow_horses', category: 'vod', aspect: '2:3' },
  { slot: 'vod_ford_v_ferrari', category: 'vod', aspect: '2:3' },
  { slot: 'vod_creed3', category: 'vod', aspect: '2:3' },
  { slot: 'vod_air', category: 'vod', aspect: '2:3' },
  { slot: 'vod_moneyball', category: 'vod', aspect: '2:3' },
  { slot: 'vod_blind_side', category: 'vod', aspect: '2:3' },
  { slot: 'vod_rush', category: 'vod', aspect: '2:3' },

  // Broadcaster logos (13 wide)
  { slot: 'broadcaster_logo_espn', category: 'broadcasters', aspect: 'wide' },
  { slot: 'broadcaster_logo_fox_sports', category: 'broadcasters', aspect: 'wide' },
  { slot: 'broadcaster_logo_tnt_sports', category: 'broadcasters', aspect: 'wide' },
  { slot: 'broadcaster_logo_tbs', category: 'broadcasters', aspect: 'wide' },
  { slot: 'broadcaster_logo_abc', category: 'broadcasters', aspect: 'wide' },
  { slot: 'broadcaster_logo_cbs_sports', category: 'broadcasters', aspect: 'wide' },
  { slot: 'broadcaster_logo_nbc_sports', category: 'broadcasters', aspect: 'wide' },
  { slot: 'broadcaster_logo_telemundo', category: 'broadcasters', aspect: 'wide' },
  { slot: 'broadcaster_logo_tudn', category: 'broadcasters', aspect: 'wide' },
  { slot: 'broadcaster_logo_beIN_sports', category: 'broadcasters', aspect: 'wide' },
  { slot: 'broadcaster_logo_tsn', category: 'broadcasters', aspect: 'wide' },
  { slot: 'broadcaster_logo_rds', category: 'broadcasters', aspect: 'wide' },
  { slot: 'broadcaster_logo_sportsnet', category: 'broadcasters', aspect: 'wide' },

  // Premium channels (9 wide)
  { slot: 'channel_hbo_max', category: 'channels', aspect: 'wide' },
  { slot: 'channel_netflix', category: 'channels', aspect: 'wide' },
  { slot: 'channel_disney_plus', category: 'channels', aspect: 'wide' },
  { slot: 'channel_apple_tv_plus', category: 'channels', aspect: 'wide' },
  { slot: 'channel_paramount_plus', category: 'channels', aspect: 'wide' },
  { slot: 'channel_peacock', category: 'channels', aspect: 'wide' },
  { slot: 'channel_showtime', category: 'channels', aspect: 'wide' },
  { slot: 'channel_starz', category: 'channels', aspect: 'wide' },
  { slot: 'channel_hulu', category: 'channels', aspect: 'wide' },

  // Testimonial avatars (8 square)
  { slot: 'testimonial_marcus', category: 'testimonials', aspect: '1:1' },
  { slot: 'testimonial_sarah', category: 'testimonials', aspect: '1:1' },
  { slot: 'testimonial_tony', category: 'testimonials', aspect: '1:1' },
  { slot: 'testimonial_carlos', category: 'testimonials', aspect: '1:1' },
  { slot: 'testimonial_jennifer', category: 'testimonials', aspect: '1:1' },
  { slot: 'testimonial_mason', category: 'testimonials', aspect: '1:1' },
  { slot: 'testimonial_aisha', category: 'testimonials', aspect: '1:1' },
  { slot: 'testimonial_david', category: 'testimonials', aspect: '1:1' },
];

/** Inferring the slot from a filename. Strips extension + lowercases.
 *  e.g. `Hero_FIFA_WC.png` → `hero_fifa_wc`, `City_Atlanta.JPG` → `city_atlanta`.
 *  Returns null when no exact match, surfacing a friendly error in the
 *  UI ("the filename should be one of: hero_fifa_wc, …"). */
export function inferSlot(filename: string): string | null {
  const stem = filename.replace(/\.[^.]+$/, '').toLowerCase();
  const match = REQUIRED_SLOTS.find((s) => s.slot.toLowerCase() === stem);
  return match ? match.slot : null;
}

export const REQUIRED_SLOT_COUNT = REQUIRED_SLOTS.length;
